import React, { useEffect, useMemo, useRef, useState } from 'react'
import { isTablet, isMobileOnly } from 'react-device-detect'
import { Box, Button, Flex, Text, Image } from 'rebass'
import { useSupportedBrowser, usePortraitScreen } from 'uneeq-react-core'
import { useTranslation, Trans } from 'react-i18next'
import styles from './styles'

import portaitImg from '../assets/landing/LandingPageVideoPortrait.jpg'
import landingImg from '../assets/landing/LandingPageVideo.jpg'
import portaitVideo from '../assets/landing/LandingPageVideoPortrait.mp4'
import landingVideo from '../assets/landing/LandingPageVideo.mp4'
import MayaUnsupportedBrowser from '../../customPlugins/MayaUnsupportedBrowser'
import MayaCloseButton from '../../customPlugins/MayaCloseButton/MayaCloseButton'

interface SophieVideoProps {
  desktopOnly: boolean
  widgetMode: boolean
}
const SophieVideo = ({ desktopOnly, widgetMode }: SophieVideoProps) => {
  const portraitScreen = usePortraitScreen()
  const isSmallScreen = isTablet || isMobileOnly

  const shouldShowPortraitVideo = useMemo(() => {
    if (desktopOnly) {
      return portraitScreen
    }

    return isSmallScreen
  }, [desktopOnly, isSmallScreen, portraitScreen])

  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load()
    }
  }, [portraitScreen])

  const vid = document.getElementById('sophieVideo')

  //loop video
  useEffect(() => {
    const restartVideo = () => {
      if (vid) {
        // @ts-ignore
        vid.currentTime = 0.1 //setting to zero breaks iOS 3.2, the value won't update, values smaller than 0.1 was causing bug as well.
        // @ts-ignore
        vid.play()
      }
    }
    // @ts-ignore
    vid && vid.addEventListener('ended', restartVideo, false)
    return () => {
      // @ts-ignore
      vid && vid.removeEventListener('ended', restartVideo)
    }
  }, [vid])

  return (
    <Box
      sx={widgetMode ? styles.sophieVideoWidget : styles.sophieVideo}
      as="video"
      ref={videoRef}
      id="sophieVideo"
      playsInline
      autoPlay
      muted
      loop
      poster={shouldShowPortraitVideo ? portaitImg : landingImg}
    >
      <source
        src={shouldShowPortraitVideo ? portaitVideo : landingVideo}
        type="video/mp4"
      />
    </Box>
  )
}

interface HomeProps {
  startSession: (speak: boolean) => void
  restart?: () => void
  headingMessage: string
  disableDigitalHuman: boolean
  logo: string
  widgetMode?: boolean
  embeddedMode: boolean
}

const Home: React.FC<HomeProps> = ({
  startSession,
  restart = () => {},
  headingMessage,
  disableDigitalHuman,
  logo,
  widgetMode = false,
  embeddedMode
}) => {
  const { isBrowserSupported } = useSupportedBrowser()
  const { t } = useTranslation()
  const [showUnsupportedAlert, setShowUnsupportedAlert] = useState(false)

  const StartButton = ({ speak, sx, ...props }: any) => {
    const handleClick =
      speak && !isBrowserSupported
        ? () => setShowUnsupportedAlert(true)
        : () => startSession(speak)

    return (
      <Button
        {...props}
        onClick={handleClick}
        sx={{ ...styles.letsChatButton, ...sx }}
      >
        {t(speak ? 'Home.audioButton' : 'Home.chatButton')}
      </Button>
    )
  }

  return (
    <Flex
      sx={{
        ...styles.mainContainer,
        ...(widgetMode
          ? {
              justifyContent: 'flex-end',
              position: 'static',
              flexDirection: 'column'
            }
          : {})
      }}
    >
      <SophieVideo widgetMode={widgetMode} desktopOnly={embeddedMode} />
      {showUnsupportedAlert && (
        <MayaUnsupportedBrowser
          close={() => setShowUnsupportedAlert(false)}
          continueChatMode={() => {
            setShowUnsupportedAlert(false)
            startSession(false)
          }}
        />
      )}
      {logo && !widgetMode && (
        <Box sx={styles.logo}>
          <Image src={logo} alt="Logo"></Image>
        </Box>
      )}
      <Flex
        sx={{
          ...styles.textContainer,
          ...(widgetMode
            ? {
                m: 0
              }
            : {})
        }}
      >
        <Text sx={styles.title}>
          <Trans i18nKey="Home.welcome" components={{ bold: <strong /> }} />
          <br />
          {headingMessage}
        </Text>

        {headingMessage && (
          <Flex sx={styles.actions}>
            {!disableDigitalHuman && (
              <StartButton variant="primary" speak={true} />
            )}
            <StartButton variant="whiteInverted" speak={false} />
          </Flex>
        )}
        <Flex>
          <Text sx={styles.subtitle}>
            <Trans
              i18nKey="Home.privacy"
              components={{
                privacylink: (
                  <Text
                    as="a"
                    href="https://mayamd.ai/privacy-policy/"
                    target="_blank"
                    rel="noreferrer noopener"
                    sx={styles.privacyPolicyLink}
                  />
                ),
                termslink: (
                  <Text
                    as="a"
                    href="https://mayamd.ai/terms-and-conditions/"
                    target="_blank"
                    rel="noreferrer noopener"
                    sx={styles.privacyPolicyLink}
                  />
                )
              }}
            />
          </Text>
          {widgetMode && (
            <MayaCloseButton
              customStyles={{ alignSelf: 'center' }}
              onClick={restart}
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default Home
